import React, { useState, useEffect, useCallback, FC, useRef } from 'react';
import { Transaction, Span, apm } from '@elastic/apm-rum';
import logger from '../../utils/logger';
import checkTableStatus from './check-table-status';
import openTable from './open-table';
import OATContext from './oat-context';
import { setItem, removeAllItems } from '../../utils/storage';
import { withConfig } from '../configProvider';
import { useOffline } from '../offlineProvider';
import useSocketOat from './use-oat-socket';
import useOatStorageCheck from './use-oat-storage-check';

const { Provider } = OATContext;

const initialOatState = {
  error: '',
  restaurantId: '',
  currentState: '',
  data: {},
  clientId: '',
};

interface OATProviderProps {
  errors?:
    | {
        code: string;
        message: string;
      }[]
    | any;
  initialStorage?: {
    clientId: string;
    restaurantId: string;
  };
  config: any;
  itemImages?: {
    id: string;
    itemImageData: GatsbyTypes.menusPricedMenusItems;
  }[];
}

const OATProvider: FC<OATProviderProps> = ({
  errors,
  initialStorage,
  config: { page, features, getMatchingPath, routes, setGetPagePath, paymentMode },
  itemImages,
  children,
}) => {
  const [socket, setSocket] = useState();
  const [isLoading, setIsLoading] = useState(true);
  const [isRedundant, setIsRedundant] = useState(false);
  const { setIsConnectTimeout } = useOffline();
  const transaction = useRef<Transaction | undefined>();
  const span = useRef<Span | undefined>();

  const [oatState, setOatState] = useState({
    ...initialOatState,
    restaurantId: initialStorage?.restaurantId,
  });

  const resetOatState = () => setOatState(initialOatState);

  const updateOatState = (newState) => {
    const { error, ...oldOatState } = oatState;

    setOatState({
      ...oldOatState,
      ...newState,
    });

    if (oldOatState.currentState === newState.currentState) {
      setIsLoading(false);
    } else if (newState.currentState) {
      if (span.current) {
        span.current.end();
      }
      if (transaction.current) {
        transaction.current.end();
      }
      transaction.current = apm.startTransaction(newState.currentState, 'state-change', {
        managed: true,
      });
      span.current = transaction.current?.startSpan(newState.currentState, 'state-change', {
        blocking: true,
      });
    }
  };

  const { restaurantId, clientId, currentState, data, error } = oatState;

  useSocketOat({
    clientId,
    removeAllItems,
    resetOatState,
    setIsConnectTimeout,
    updateOatState,
    restaurantId,
    setIsLoading,
    setSocket,
    currentState,
    setIsRedundant,
    paymentMode,
    features,
  });

  const getItemImageData = useCallback(
    (itemId) => {
      const itemImage = itemImages?.find((item) => item.id === itemId);
      return itemImage?.itemImageData;
    },
    [itemImages]
  );

  useEffect(() => {
    setGetPagePath(() => (nextPage) => {
      const urlSearchParams = new URLSearchParams(window.location.search);
      const urlRestaurantId = urlSearchParams.get('restaurantId');

      const route = routes?.find(({ path }) => path === 'order-at-table');
      const matchingPath = route && getMatchingPath(route as any)(nextPage);

      return matchingPath?.replace('{id}', restaurantId || urlRestaurantId);
    });
    // eslint-disable-next-line no-autofix/react-hooks/exhaustive-deps
  }, [setGetPagePath, restaurantId]);

  const updateState = (remoteClientId, stateRestaurantId) => {
    setIsLoading(true);

    const info = {
      clientId: remoteClientId,
      restaurantId: stateRestaurantId,
    };

    setItem(info);

    updateOatState(info);
  };

  const restoreOriginalState = (joinTable) => {
    removeAllItems();
    updateOatState({
      ...(joinTable && initialOatState),
      error: errors.unableToJoinTable || '',
    });
  };

  const queryTableStatus = async (
    stateRestaurantId: string,
    inputPaymentMode: boolean,
    urlSearchParams: URLSearchParams,
    joinTable = false
  ) => {
    try {
      const { clientId: remoteClientId } = await checkTableStatus(
        stateRestaurantId,
        inputPaymentMode,
        urlSearchParams
      );

      updateState(remoteClientId, stateRestaurantId);
    } catch (e) {
      logger.info({ msg: 'table not found', clientId });
      restoreOriginalState(joinTable);
    }
  };

  const joinTable = async (
    stateRestaurantId: string,
    inputPaymentMode: boolean,
    tableNumber: string
  ) => {
    try {
      const { clientId: remoteClientId } = await openTable(
        stateRestaurantId,
        tableNumber,
        inputPaymentMode
      );

      updateState(remoteClientId, stateRestaurantId);
    } catch (e) {
      logger.info({ msg: 'table not found', clientId });
      restoreOriginalState(true);
    }
  };

  useEffect(() => {
    if (error) {
      setIsLoading(false);
    }
  }, [error]);

  useOatStorageCheck({
    initialStorage,
    updateOatState,
    removeAllItems,
    page,
  });

  return (
    <Provider
      value={{
        getItemImageData,
        socket,
        currentState,
        data,
        error,
        features,
        queryTableStatus,
        joinTable,
        initialStorage,
        clientId,
        setIsLoading,
        isLoading,
        isRedundant,
        restaurantId,
      }}
    >
      {children}
    </Provider>
  );
};

OATProvider.defaultProps = {
  errors: [],
};

export default withConfig(OATProvider);
